// Customer accounts (roadmap Phase 1.5): balances, aging, statements — all worked out from the
// app's own /invoices and /payments (what functions/invoices.js and payments.js saved), never
// stored separately, so they can't drift.
//
//   balance = what's owed on open invoices − credit (unapplied money on payments)
//   aging   = each open invoice's balance by days past its due date:
//             current (not yet due) · 1–30 · 31–60 · 61–90 · 90+
//
//   summarize(invoices, payments, today)  → Map customerId → account
//   ledger(customerId, invoices, payments) → dated rows with a running balance
//   renderAccountsTab(box) / openStatement(customerId) / emailStatements(customerIds)
//   Late payments (1.6, collectionsCore.js): "Finance charges…" adds this month's charges (server
//   assessFinanceCharges); "Send reminders" emails the overdue reminder (Settings › Pricing) with
//   the statement attached, then markReminded notes it on the invoices.

import { database, functions } from './initFirebase';
import { ref, get } from 'firebase/database';
import { deviceCopy } from './deviceCopy';
import { httpsCallable } from 'firebase/functions';
import { billingTerms, termsLine, overdueByCustomer, reminderMessage, financeOn } from './collectionsCore';
import { toastMessage, escapeHTML } from './utilities';
import { formatMoney } from './money';
import { BUCKETS, todayYmd, bucketFor, summarize, ledger, daysBetween, fmtDate, accountTotals } from './accountsCore';
export { BUCKETS, todayYmd, bucketFor, summarize, ledger };
import { openCompose, pagesToPdf, businessProfile, sendMail } from './mailClient';

const esc = v => escapeHTML(v == null ? '' : String(v));
const call = (name, data) => httpsCallable(functions, name, { timeout: 120000 })(data).then(r => r.data);
const changed = () => window.dispatchEvent(new CustomEvent('invoices:changed'));

export async function loadAccounts() {
  const [invoices, payments, terms] = await Promise.all([
    deviceCopy('invoices').all(),
    deviceCopy('payments').all(),
    get(ref(database, 'billingTerms')).then(s => s.val() || {}).catch(() => ({})),
  ]);
  return { invoices, payments, terms, accounts: summarize(invoices, payments) };
}

/* ============ statement ============ */

// The statement: an account summary (all-time invoiced, paid, amount due) so it reconciles with the
// Payments list, the open invoices with what's been paid on each, and the last 60 days' payments with
// the invoices they went to (earlier ones are counted in the summary).
function statementHTML(a, data, biz, today) {
  const mine = Object.values(data.payments || {}).filter(p => p?.customerId === a.customerId && p.status === 'ok');
  const recent = mine.filter(p => daysBetween(p.receivedDate || '', today) <= 60)
    .sort((x, y) => String(x.receivedDate).localeCompare(String(y.receivedDate)));
  const earlier = mine.filter(p => !recent.includes(p));
  const earlierCents = earlier.reduce((s, p) => s + (Number(p.amountCents) || 0), 0);
  const tot = accountTotals(a.customerId, data.invoices, data.payments, a.balanceCents);
  const appliedTo = p => [
    ...Object.keys(p.applications || {}).map(id => data.invoices?.[id]?.number).filter(Boolean),
    ...(p.unappliedCents > 0 ? [`${formatMoney(p.unappliedCents)} credit`] : []),
  ].join(', ');
  return `
    <div class="invDoc-page" data-statement="${esc(a.customerId)}">
      <div class="estDoc-letterhead">
        <img src="/images/logo/logo-512.webp" class="estDoc-logo" alt="">
        <div class="estDoc-title-block">
          <div class="invDoc-title-row"><div class="estDoc-title">STATEMENT</div></div>
          <div class="estDoc-number">${esc(fmtDate(today))}</div>
        </div>
      </div>
      <div class="estDoc-meta-row">
        <div class="estDoc-meta-col estDoc-meta-col-wide">
          <div class="estDoc-meta-label">Account</div>
          <div class="estDoc-meta-name">${esc(a.name)}</div>
          <div class="estDoc-meta-sub">${esc(a.email)}</div>
        </div>
        <div class="estDoc-meta-dates">
          <div class="estDoc-meta-dates-labels"><div>Statement date</div><div>Overdue</div><div>Amount due</div></div>
          <div class="estDoc-meta-dates-values"><div>${esc(fmtDate(today))}</div><div>${formatMoney(a.overdueCents)}</div><div><b>${formatMoney(Math.max(0, a.balanceCents))}</b></div></div>
        </div>
      </div>
      <table class="invDoc-line-table">
        <thead><tr><th>Account summary</th><th class="estDoc-num">Amount</th></tr></thead>
        <tbody>
          <tr><td><div class="invDoc-line-name">Invoiced</div><div class="invDoc-line-sub">${tot.since ? `Since ${esc(fmtDate(tot.since))}` : 'No invoices yet'}</div></td><td class="estDoc-num">${formatMoney(tot.invoicedCents)}</td></tr>
          <tr><td><div class="invDoc-line-name">Payments received</div><div class="invDoc-line-sub">${mine.length} payment${mine.length === 1 ? '' : 's'}</div></td><td class="estDoc-num">-${formatMoney(tot.paidCents)}</td></tr>
          ${tot.adjustCents ? `<tr><td><div class="invDoc-line-name">Other adjustments</div></td><td class="estDoc-num">${tot.adjustCents < 0 ? '-' : ''}${formatMoney(Math.abs(tot.adjustCents))}</td></tr>` : ''}
          <tr><td><div class="invDoc-line-name"><b>${a.balanceCents < 0 ? 'Credit balance' : 'Amount due'}</b></div></td><td class="estDoc-num"><b>${formatMoney(Math.abs(a.balanceCents))}</b></td></tr>
        </tbody>
      </table>
      <table class="invDoc-line-table">
        <thead><tr><th>Open invoices</th><th class="estDoc-num">Due</th><th class="estDoc-num">Invoice total</th><th class="estDoc-num">Paid</th><th class="estDoc-num">Balance</th></tr></thead>
        <tbody>
          ${a.openInvoices.map(i => {
            const late = i.dueDate ? daysBetween(i.dueDate, today) : 0;
            return `<tr><td><div class="invDoc-line-name">${esc(i.number)}</div><div class="invDoc-line-sub">${i.issuedDate ? `Issued ${esc(fmtDate(i.issuedDate))}` : ''}${i.boatName ? ` · ${esc(i.boatName)}` : ''}${late > 0 ? ` · <b>${late} days past due</b>` : ''}</div></td>
              <td class="estDoc-num">${esc(fmtDate(i.dueDate))}</td><td class="estDoc-num">${formatMoney(i.totalCents)}</td><td class="estDoc-num">${formatMoney(i.paidCents || 0)}</td><td class="estDoc-num">${formatMoney(i.balanceCents)}</td></tr>`;
          }).join('') || '<tr><td colspan="5"><div class="invDoc-line-sub">No open invoices.</div></td></tr>'}
        </tbody>
      </table>
      ${mine.length ? `<table class="invDoc-line-table">
        <thead><tr><th>Payments received (last 60 days)</th><th class="estDoc-num">Date</th><th class="estDoc-num">Amount</th></tr></thead>
        <tbody>${recent.map(p => `<tr><td><div class="invDoc-line-name">${esc(p.number)}</div><div class="invDoc-line-sub">${esc([p.method, appliedTo(p) && `Applied to ${appliedTo(p)}`].filter(Boolean).join(' · '))}</div></td><td class="estDoc-num">${esc(fmtDate(p.receivedDate))}</td><td class="estDoc-num">${formatMoney(p.amountCents)}</td></tr>`).join('')}
          ${earlier.length ? `<tr><td colspan="2"><div class="invDoc-line-sub">${recent.length ? 'Plus ' : ''}${earlier.length} earlier payment${earlier.length === 1 ? '' : 's'}, counted in the account summary above</div></td><td class="estDoc-num">${formatMoney(earlierCents)}</td></tr>` : ''}</tbody>
      </table>` : ''}
      <div class="estDoc-totals-stack invDoc-totals-stack">
        ${BUCKETS.map(([k, l]) => a.buckets[k] ? `<div class="estDoc-totals-row"><span>${k === 'current' ? 'Not yet due' : `${l} days past due`}</span><span>${formatMoney(a.buckets[k])}</span></div>` : '').join('')}
        ${a.creditCents ? `<div class="estDoc-totals-row"><span>Credit on account</span><span>-${formatMoney(a.creditCents)}</span></div>` : ''}
        <div class="invDoc-balance-due-row"><span>${a.balanceCents < 0 ? 'CREDIT BALANCE' : 'AMOUNT DUE'}</span><span>${formatMoney(Math.abs(a.balanceCents))}</span></div>
      </div>
      <div class="estDoc-terms-block invDoc-terms-block">
        <div class="estDoc-terms-label">Payment</div>
        <div class="estDoc-terms-text">Please reference the invoice numbers with your payment. If you've paid recently, thank you, and please disregard this statement.${termsLine(data.terms) ? ` ${esc(termsLine(data.terms))}` : ''}</div>
      </div>
      <div class="estDoc-footer">
        <span>${esc([biz.name, biz.address, biz.phone, biz.supportEmail].filter(Boolean).join(' · '))}</span>
        <span class="estDoc-page-num">Page 1 of 1</span>
      </div>
    </div>`;
}

function statementContainer() {
  document.getElementById('statementContainer1')?.remove();
  const box = document.createElement('div');
  box.id = 'statementContainer1';
  box.className = 'estimateContainer';
  document.body.appendChild(box);
  return box;
}

const statementEmail = (a, biz, today) => ({
  subject: `Statement of account${biz.name ? ` from ${biz.name}` : ''} — ${fmtDate(today)}`,
  body: `${a.name ? `Hi ${a.name.split(' ')[0]},` : 'Hello,'}\n\nAttached is your statement as of ${fmtDate(today)}. ${a.balanceCents > 0 ? `The amount due is ${formatMoney(a.balanceCents)}${a.overdueCents ? `, of which ${formatMoney(a.overdueCents)} is past due` : ''}.` : 'Your account is paid up — thank you.'}\n\nReply to this email or give us a call with any questions.\n\nThank you,`,
});

/** Show one customer's statement (Print / Email / Close). onClose runs when it's closed. */
export async function openStatement(customerId, { onClose } = {}) {
  const [data, biz] = await Promise.all([loadAccounts(), businessProfile()]);
  const a = data.accounts.get(customerId);
  if (!a) { toastMessage('No invoices or payments for this customer yet.', 'red'); return; }
  const today = todayYmd();
  const box = statementContainer();
  box.innerHTML = `
    <div class="estimatePDF">
      <div class="no-print estDoc-toolbar">
        <button type="button" data-s="print" class="rcpt-btn-primary">Print / Download PDF</button>
        <button type="button" data-s="email" class="estDoc-email-btn">Email to customer</button>
        <button type="button" data-s="close" class="rcpt-btn">Close</button>
      </div>
      ${statementHTML(a, data, biz, today)}
    </div>`;
  const list = document.getElementById('inv-list-overlay');
  if (list) list.style.display = 'none';
  const close = () => { box.remove(); if (list?.isConnected) list.style.display = ''; onClose?.(); };
  box.addEventListener('click', e => {
    const s = e.target.closest('[data-s]')?.dataset.s;
    if (s === 'close') close();
    if (s === 'print') window.print();
    if (s === 'email') {
      const msg = statementEmail(a, biz, today);
      openCompose({
        title: `Email statement — ${a.name}`, to: a.email, subject: msg.subject, body: msg.body,
        attachments: pagesToPdf([box.querySelector('.invDoc-page')], `Statement ${today} ${a.name}.pdf`),
        customerId, docType: 'statement', docLabel: `Statement ${today}`, recordType: 'customer', recordId: customerId,
      });
    }
  });
}

/**
 * Email each chosen customer their statement, one after another (PDF made from the page, sent
 * from the company account). Returns { sent, skipped, failed }. onProgress(done, total, name).
 * reminder: true sends the overdue reminder (Settings › Pricing) instead of the statement note,
 * only to customers with something past due, and notes the reminder on their invoices.
 */
export async function emailStatements(customerIds, { onProgress, reminder = false } = {}) {
  const [data, biz] = await Promise.all([loadAccounts(), businessProfile()]);
  const today = todayYmd();
  const overdue = reminder ? overdueByCustomer(data.invoices, today) : null;
  const box = statementContainer();
  const result = { sent: [], skipped: [], failed: [] };
  let done = 0;
  for (const id of customerIds) {
    const a = data.accounts.get(id);
    const late = overdue?.get(id);
    const why = !a ? 'no account' : !a.email ? 'no email' : reminder && !late ? 'nothing past due' : '';
    if (why) { result.skipped.push(`${a?.name || id} (${why})`); onProgress?.(++done, customerIds.length, a?.name); continue; }
    box.innerHTML = `<div class="estimatePDF">${statementHTML(a, data, biz, today)}</div>`;
    try {
      const pdf = await pagesToPdf([box.querySelector('.invDoc-page')], `Statement ${today} ${a.name}.pdf`);
      const msg = reminder ? reminderMessage({ ...late, name: a.name }, data.terms, biz.name) : statementEmail(a, biz, today);
      await sendMail({
        to: a.email, subject: msg.subject,
        layout: reminder ? { text: msg.text, signature: false } : { text: msg.body },
        attachments: [{ filename: pdf.filename, contentType: pdf.contentType, base64: pdf.base64 }],
        customerId: id, recordType: 'customer', recordId: id, docType: reminder ? 'reminder' : 'statement', docLabel: `${reminder ? 'Reminder' : 'Statement'} ${today}`,
      });
      if (reminder) await call('markReminded', { customerId: id }).catch(err => console.warn('[accounts] could not note the reminder', err));
      result.sent.push(a.name);
    } catch (err) {
      result.failed.push(`${a.name}: ${String(err?.message || err)}`);
    }
    onProgress?.(++done, customerIds.length, a.name);
  }
  box.remove();
  if (reminder && result.sent.length) changed();
  return result;
}

/* ============ finance charges ============ */

/** Preview this month's finance charges, untick any to skip, and add them (server-side). */
export async function openFinanceCharges({ onDone } = {}) {
  document.getElementById('fin-dialog')?.remove();
  const wrap = document.createElement('div');
  wrap.id = 'fin-dialog';
  wrap.className = 'inv-root inv-backdrop pay-backdrop';
  document.body.appendChild(wrap);
  const close = () => wrap.remove();
  const shell = (body, foot) => {
    wrap.innerHTML = `
      <div class="inv-panel pay-panel" role="dialog" aria-modal="true" aria-labelledby="fin-title">
        <div class="inv-head"><div class="inv-title" id="fin-title">Finance charges</div>
          <button type="button" class="inv-x" data-f="close" aria-label="Close">&times;</button></div>
        <div class="inv-body inv-ed-body">${body}</div>
        <div class="inv-foot"><span class="inv-spacer"></span>${foot}</div>
      </div>`;
  };
  wrap.addEventListener('click', e => { if (e.target === wrap || e.target.closest('[data-f="close"]')) close(); });
  shell('<div class="inv-muted">Working out this month\u2019s charges…</div>', '<button type="button" class="inv-btn" data-f="close">Cancel</button>');

  const terms = billingTerms((await get(ref(database, 'billingTerms')).catch(() => null))?.val());
  if (!financeOn(terms)) {
    shell(`<p>Finance charges are off. An admin can turn them on, and set the rate, in <b>Settings › Pricing › Invoices &amp; late payments</b>.</p>`,
      '<button type="button" class="inv-btn" data-f="close">Close</button>');
    return;
  }
  let preview;
  try { preview = await call('assessFinanceCharges', { dryRun: true }); } catch (err) {
    shell(`<p class="inv-warn">${esc(err.message || err)}</p>`, '<button type="button" class="inv-btn" data-f="close">Close</button>');
    return;
  }
  const list = preview.customers || [];
  if (!list.length) {
    shell(`<p>No finance charges to add for ${esc(monthName(preview.period))}. Past-due invoices already charged this month aren\u2019t charged again${terms.graceDays ? `, and invoices less than ${terms.graceDays} days late are left alone` : ''}.</p>`,
      '<button type="button" class="inv-btn" data-f="close">Close</button>');
    return;
  }
  const pick = new Set(list.map(c => c.customerId));
  const draw = () => {
    const total = list.filter(c => pick.has(c.customerId)).reduce((s, c) => s + c.totalCents, 0);
    shell(`
      <p class="inv-muted">${terms.financeRatePct}% of each past-due balance${terms.graceDays ? ` (more than ${terms.graceDays} days late)` : ''}, once for ${esc(monthName(preview.period))}. Each customer gets one finance-charge invoice, due on receipt. Untick anyone to skip.</p>
      <table class="inv-lines fin-table"><tbody>
        ${list.map(c => `<tr class="fin-cust"><td class="inv-c"><input type="checkbox" data-fpick="${esc(c.customerId)}"${pick.has(c.customerId) ? ' checked' : ''} aria-label="Charge ${esc(c.name)}"></td>
          <td><b>${esc(c.name)}</b>${c.lines.map(l => `<div class="inv-muted">${esc(l.number)}: ${formatMoney(l.pastDueCents)}, ${l.daysLate} days late</div>`).join('')}</td>
          <td class="inv-num"><b>${formatMoney(c.totalCents)}</b></td></tr>`).join('')}
      </tbody></table>
      <div class="inv-muted" data-f="status"></div>`,
    `<button type="button" class="inv-btn" data-f="close">Cancel</button>
     <button type="button" class="inv-btn inv-primary" data-f="add"${pick.size ? '' : ' disabled'}>Add ${pick.size} charge${pick.size === 1 ? '' : 's'} (${formatMoney(total)})</button>`);
  };
  draw();
  wrap.addEventListener('change', e => {
    const id = e.target.dataset.fpick;
    if (!id) return;
    e.target.checked ? pick.add(id) : pick.delete(id);
    draw();
  });
  wrap.addEventListener('click', async e => {
    if (!e.target.closest('[data-f="add"]')) return;
    e.target.disabled = true;
    wrap.querySelector('[data-f="status"]').textContent = 'Adding…';
    try {
      const res = await call('assessFinanceCharges', { customerIds: [...pick] });
      toastMessage(`Finance charges added: ${res.created.length} invoice${res.created.length === 1 ? '' : 's'}`, 'green', 5000);
      changed();
      close();
      onDone?.(res);
    } catch (err) {
      wrap.querySelector('[data-f="status"]').innerHTML = `<span class="inv-warn">${esc(err.message || err)}</span>`;
      e.target.disabled = false;
    }
  });
}

function monthName(period) {
  const [y, m] = String(period).split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/* ============ Accounts tab (the aging report) ============ */

let sortKey = 'balance';
let chosen = new Set();

/** The Accounts tab of the Invoices window: aging by customer, statements, CSV. */
export async function renderAccountsTab(box, search) {
  const data = await loadAccounts();
  const q = String(search || '').trim().toLowerCase();
  const rows = [...data.accounts.values()]
    .filter(a => a.balanceCents !== 0 || a.creditCents)
    .filter(a => !q || a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q));
  const sorters = {
    balance: (x, y) => y.balanceCents - x.balanceCents,
    overdue: (x, y) => y.overdueCents - x.overdueCents,
    name: (x, y) => x.name.localeCompare(y.name),
  };
  rows.sort(sorters[sortKey] || sorters.balance);
  const tot = Object.fromEntries(BUCKETS.map(([k]) => [k, rows.reduce((s, a) => s + a.buckets[k], 0)]));
  const totCredit = rows.reduce((s, a) => s + a.creditCents, 0);
  const totBal = rows.reduce((s, a) => s + a.balanceCents, 0);
  [...chosen].forEach(id => { if (!rows.some(a => a.customerId === id)) chosen.delete(id); });
  const chosenLate = [...chosen].filter(id => data.accounts.get(id)?.overdueCents > 0).length;
  const reminded = a => Math.max(0, ...a.openInvoices.map(i => Number(i.lastReminderAt) || 0));

  box.innerHTML = rows.length ? `
    <div class="acct-bar">
      <label class="inv-muted">Sort <select data-acct="sort">
        <option value="balance"${sortKey === 'balance' ? ' selected' : ''}>Balance</option>
        <option value="overdue"${sortKey === 'overdue' ? ' selected' : ''}>Most overdue</option>
        <option value="name"${sortKey === 'name' ? ' selected' : ''}>Name</option></select></label>
      <span class="inv-spacer"></span>
      <button type="button" class="inv-btn inv-small" data-acct="csv">Export CSV</button>
      <button type="button" class="inv-btn inv-small" data-acct="finance">Finance charges…</button>
      <button type="button" class="inv-btn inv-small" data-acct="remind"${chosenLate ? '' : ' disabled'}>Send reminders (${chosenLate})</button>
      <button type="button" class="inv-btn inv-primary inv-small" data-acct="email"${chosen.size ? '' : ' disabled'}>Email statements (${chosen.size})</button>
    </div>
    <div class="acct-scroll"><table class="inv-lines acct-table">
      <thead><tr><th class="inv-c"><input type="checkbox" data-acct="all" aria-label="Select all"${chosen.size && chosen.size === rows.filter(r => r.balanceCents > 0).length ? ' checked' : ''}></th><th>Customer</th>
        ${BUCKETS.map(([, l]) => `<th class="inv-num">${l}</th>`).join('')}<th class="inv-num">Credit</th><th class="inv-num">Balance</th><th></th></tr></thead>
      <tbody>
        ${rows.map(a => `<tr>
          <td class="inv-c"><input type="checkbox" data-pick="${esc(a.customerId)}"${chosen.has(a.customerId) ? ' checked' : ''}${a.balanceCents > 0 ? '' : ' disabled'} aria-label="Select ${esc(a.name)}"></td>
          <td><b>${esc(a.name)}</b>${a.email ? '' : ' <span class="inv-warn">no email</span>'}${reminded(a) ? `<div class="inv-muted acct-reminded">Reminded ${esc(new Date(reminded(a)).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))}</div>` : ''}</td>
          ${BUCKETS.map(([k]) => `<td class="inv-num${k !== 'current' && a.buckets[k] ? ' acct-late' : ''}">${a.buckets[k] ? formatMoney(a.buckets[k]) : ''}</td>`).join('')}
          <td class="inv-num">${a.creditCents ? `-${formatMoney(a.creditCents)}` : ''}</td>
          <td class="inv-num"><b>${formatMoney(a.balanceCents)}</b></td>
          <td><button type="button" class="inv-link" data-statement="${esc(a.customerId)}">Statement</button></td>
        </tr>`).join('')}
        <tr class="acct-total"><td></td><td>Total</td>${BUCKETS.map(([k]) => `<td class="inv-num">${formatMoney(tot[k])}</td>`).join('')}
          <td class="inv-num">${totCredit ? `-${formatMoney(totCredit)}` : ''}</td><td class="inv-num">${formatMoney(totBal)}</td><td></td></tr>
      </tbody>
    </table></div>
    <div class="inv-muted acct-progress" data-acct="progress"></div>` : '<div class="inv-empty">Nobody owes anything. Balances appear here once invoices are issued.</div>';

  box.onchange = e => {
    const t = e.target;
    if (t.dataset.acct === 'sort') { sortKey = t.value; renderAccountsTab(box, search); }
    if (t.dataset.acct === 'all') { rows.filter(r => r.balanceCents > 0).forEach(r => (t.checked ? chosen.add(r.customerId) : chosen.delete(r.customerId))); renderAccountsTab(box, search); }
    if (t.dataset.pick) { t.checked ? chosen.add(t.dataset.pick) : chosen.delete(t.dataset.pick); renderAccountsTab(box, search); }
  };
  box.onclick = async e => {
    const st = e.target.closest('[data-statement]')?.dataset.statement;
    if (st) return openStatement(st);
    const act = e.target.closest('[data-acct]')?.dataset.acct;
    if (act === 'csv') return downloadCsv(rows);
    if (act === 'finance') return openFinanceCharges({ onDone: () => renderAccountsTab(box, search) });
    if (act === 'email' || act === 'remind') {
      const reminder = act === 'remind';
      const ids = reminder ? [...chosen].filter(id => data.accounts.get(id)?.overdueCents > 0) : [...chosen];
      const noEmail = ids.filter(id => !data.accounts.get(id)?.email).length;
      const n = ids.length - noEmail;
      if (!confirm(`Email ${reminder ? 'an overdue reminder (with their statement)' : 'a statement'} to ${n} customer${n === 1 ? '' : 's'}?${noEmail ? `\n\n${noEmail} without an email address will be skipped.` : ''}`)) return;
      const prog = box.querySelector('[data-acct="progress"]');
      e.target.disabled = true;
      const res = await emailStatements(ids, { reminder, onProgress: (i, total, name) => { prog.textContent = `Sending ${i} of ${total}… ${name || ''}`; } });
      prog.innerHTML = `Sent ${res.sent.length}.${res.skipped.length ? ` Skipped: ${esc(res.skipped.join(', '))}.` : ''}${res.failed.length ? ` <span class="inv-warn">Failed: ${esc(res.failed.join('; '))}</span>` : ''}`;
      toastMessage(`${reminder ? 'Reminders' : 'Statements'} sent: ${res.sent.length}`, res.failed.length ? 'red' : 'green', 5000);
      chosen = new Set();
      e.target.disabled = false;
    }
  };
}

function downloadCsv(rows) {
  const q = v => `"${String(v).replace(/"/g, '""')}"`;
  const dollars = c => (c / 100).toFixed(2);
  const head = ['Customer', 'Email', ...BUCKETS.map(([, l]) => l), 'Credit', 'Balance'];
  const lines = [head.map(q).join(',')].concat(rows.map(a => [a.name, a.email, ...BUCKETS.map(([k]) => dollars(a.buckets[k])), dollars(a.creditCents), dollars(a.balanceCents)].map(q).join(',')));
  const blob = new Blob([lines.join('\r\n')], { type: 'text/csv' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `aging-${todayYmd()}.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
}
